import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { cores } from '../../theme';

import {
    Typography, Container, Box, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, Alert, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Avatar,
    Fade, DialogContentText, Chip, Divider, Menu, MenuItem, ListItemIcon, ListItemText, useTheme
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack,
    Add as AddIcon,
    Close as CloseIcon,
    Grading as GradingIcon,
    Warning as WarningIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';

const CriteriosGerenciamentoPagina = () => {
    const [criterios, setCriterios] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [modalAberto, setModalAberto] = useState(false);
    const [editando, setEditando] = useState(null);
    const [descricao, setDescricao] = useState('');
    const [confirmarExclusao, setConfirmarExclusao] = useState({ aberto: false, criterioId: null, descricao: '' });
    const [menuAnchorEl, setMenuAnchorEl] = useState({
        id: null,
        element: null
    });
    const navegar = useNavigate();
    const theme = useTheme();

    const buscarCriterios = useCallback(async () => {
        try {
            const resposta = await api.get('/api/criterios');
            setCriterios(resposta.data);
        } catch (err) {
            setErro('Falha ao carregar os critérios.');
        } finally {
            if (carregando) setCarregando(false);
        }
    }, [carregando]);

    useEffect(() => {
        buscarCriterios();
    }, [buscarCriterios]);

    const abrirMenuAcoes = (event, criterioId) => {
        setMenuAnchorEl({
            id: criterioId,
            element: event.currentTarget
        });
    };

    const fecharMenuAcoes = () => {
        setMenuAnchorEl({
            id: null,
            element: null
        });
    };

    const abrirModalParaCriar = () => {
        setEditando(null);
        setDescricao('');
        setErro('');
        setModalAberto(true);
    };

    const abrirModalParaEditar = (criterio) => {
        setEditando(criterio);
        setDescricao(criterio.descricao);
        setErro('');
        setModalAberto(true);
        fecharMenuAcoes();
    };

    const fecharModal = () => {
        setModalAberto(false);
        setEditando(null);
        setDescricao('');
    };

    const lidarComSalvar = async () => {
        if (!descricao) return setErro("A descrição não pode estar vazia.");
        try {
            if (editando) {
                await api.put(`/api/criterios/${editando.id}`, { descricao });
            } else {
                await api.post('/api/criterios/criar', [{ descricao }]);
            }
            fecharModal();
            buscarCriterios();
        } catch (err) {
            setErro(err.response?.data?.erro || "Ocorreu um erro ao salvar.");
        }
    };

    const abrirConfirmacaoExclusao = (criterioId, descricaoCriterio) => {
        setConfirmarExclusao({
            aberto: true,
            criterioId,
            descricao: descricaoCriterio
        });
        fecharMenuAcoes();
    };

    const fecharConfirmacaoExclusao = () => {
        setConfirmarExclusao({
            aberto: false,
            criterioId: null,
            descricao: ''
        });
    };

    const lidarComDelecao = async () => {
        try {
            await api.delete(`/api/criterios/${confirmarExclusao.criterioId}`);
            fecharConfirmacaoExclusao();
            buscarCriterios();
        } catch (err) {
            setErro(err.response?.data?.erro || "Erro ao deletar critério.");
            fecharConfirmacaoExclusao();
        }
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Biblioteca de Critérios
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                Gerencie os critérios de avaliação utilizados nos casos clínicos
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                    }}>
                        <Box>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    color: cores.primaria.principal,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                }}
                            >
                                Lista de Critérios
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {criterios.length} {criterios.length === 1 ? 'critério encontrado' : 'critérios encontrados'}
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            onClick={abrirModalParaCriar}
                            startIcon={<AddIcon />}
                            sx={{
                                bgcolor: cores.primaria.principal,
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    bgcolor: cores.primaria.escuro,
                                }
                            }}
                        >
                            Adicionar Novo Critério
                        </Button>
                    </Box>

                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Fade in={true} timeout={500}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                overflow: 'hidden',
                                border: `1px solid ${cores.cinza.claro}`,
                            }}
                        >
                            <Table>
                                <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal, width: '80%' }}>
                                            Descrição do Critério
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: cores.primaria.principal }}>
                                            Ações
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {criterios.map((criterio, index) => (
                                        <Fade in={true} timeout={300 + index * 100} key={criterio.id}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: '#f8fafc',
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Avatar
                                                            sx={{
                                                                mr: 2,
                                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                                color: cores.primaria.principal,
                                                                width: 36,
                                                                height: 36
                                                            }}
                                                        >
                                                            <GradingIcon />
                                                        </Avatar>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                fontSize: '1rem',
                                                                color: cores.cinza.escuro
                                                            }}
                                                        >
                                                            {criterio.descricao}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        onClick={(e) => abrirMenuAcoes(e, criterio.id)}
                                                        size="small"
                                                        sx={{
                                                            border: `1px solid ${cores.cinza.claro}`,
                                                            '&:hover': {
                                                                bgcolor: '#f1f5f9',
                                                            }
                                                        }}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        </Fade>
                                    ))}
                                    {criterios.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" sx={{ color: cores.cinza.medio }}>
                                                    Nenhum critério encontrado.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Fade>
                </Paper>
            </Fade>

            {/* Modal para criar/editar critério */}
            <Dialog
                open={modalAberto}
                onClose={fecharModal}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`, color: cores.primaria.principal }}>
                            {editando ? <EditIcon /> : <AddIcon />}
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {editando ? 'Editar Critério' : 'Criar Novo Critério'}
                        </Typography>
                    </Box>
                    <IconButton onClick={fecharModal} size="small" sx={{ color: cores.cinza.medio }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 3 }}>
                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Typography
                        variant="body2"
                        sx={{ mb: 2, color: cores.cinza.medio }}
                    >
                        {editando
                            ? 'Atualize a descrição do critério de avaliação.'
                            : 'Digite a descrição para o novo critério de avaliação.'
                        }
                    </Typography>

                    <TextField
                        autoFocus
                        label="Descrição do Critério"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={descricao}
                        onChange={(e) => setDescricao(e.target.value)}
                        InputProps={{
                            sx: { borderRadius: 2 }
                        }}
                        helperText="Exemplo: Capacidade de realizar diagnóstico diferencial baseado nos sintomas apresentados"
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={fecharModal}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: cores.cinza.claro,
                            color: cores.cinza.escuro,
                            '&:hover': {
                                borderColor: cores.cinza.medio,
                                bgcolor: 'rgba(203, 213, 225, 0.1)'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComSalvar}
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            bgcolor: cores.primaria.principal,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: cores.primaria.escuro,
                            }
                        }}
                    >
                        {editando ? 'Atualizar' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de confirmação de exclusão */}
            <Dialog
                open={confirmarExclusao.aberto}
                onClose={fecharConfirmacaoExclusao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: cores.erro.claro,
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    py: 2
                }}>
                    <WarningIcon color="error" fontSize="small" />
                    Confirmar Exclusão
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <DialogContentText sx={{ color: cores.cinza.escuro, mb: 2 }}>
                        Você está prestes a excluir o critério:
                    </DialogContentText>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: '#f8fafc',
                            borderRadius: 2,
                            border: `1px solid ${cores.cinza.claro}`,
                            mb: 2
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: cores.cinza.escuro,
                                fontStyle: 'italic'
                            }}
                        >
                            "{confirmarExclusao.descricao}"
                        </Typography>
                    </Paper>
                    <DialogContentText sx={{
                        color: cores.erro.escuro,
                        bgcolor: cores.erro.claro,
                        p: 2,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <WarningIcon fontSize="small" sx={{ color: cores.erro.medio }} />
                        Esta ação não poderá ser desfeita. Critérios já associados a casos clínicos poderão ser afetados.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={fecharConfirmacaoExclusao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            color: cores.cinza.escuro,
                            borderColor: cores.cinza.claro,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: cores.cinza.medio
                            },
                            px: 2
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComDelecao}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            px: 3,
                            '&:hover': {
                                backgroundColor: cores.erro.escuro,
                                boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.3)`
                            }
                        }}
                    >
                        Excluir Critério
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Menu de ações */}
            <Menu
                anchorEl={menuAnchorEl.element}
                open={Boolean(menuAnchorEl.element)}
                onClose={fecharMenuAcoes}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        borderRadius: 2,
                        minWidth: 180,
                        boxShadow: cores.sombras.medio,
                        '& .MuiList-root': {
                            py: 1
                        }
                    }
                }}
            >
                <MenuItem
                    onClick={() => {
                        const criterio = criterios.find(c => c.id === menuAnchorEl.id);
                        abrirModalParaEditar(criterio);
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" sx={{ color: cores.primaria.principal }} />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const criterio = criterios.find(c => c.id === menuAnchorEl.id);
                        abrirConfirmacaoExclusao(menuAnchorEl.id, criterio?.descricao || '');
                    }}
                    sx={{
                        py: 1,
                        color: cores.erro.principal
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: cores.erro.principal }} />
                    </ListItemIcon>
                    <ListItemText>Excluir</ListItemText>
                </MenuItem>
            </Menu>
        </Container>
    );
};

export default CriteriosGerenciamentoPagina;